import { App, Form, Input, Modal, Select } from 'antd';
import { useCreateSecret } from '../hooks/useSecrets';
import {
  buildSecret,
  endpointPlaceholder,
  STORAGE_TYPE_OPTIONS,
  type StorageTypeCase,
} from '@/lib/secretStorage';
import { fullErrorText } from '@/api/errors';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  name: string;
  storageType: StorageTypeCase;
  region: string;
  endpoint: string;
  accessKey: string;
  secretKey: string;
}

const ENDPOINT_PATTERN = /^https?:\/\//;

export default function CreateObjectStorageCredentialDialog({ open, onClose }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const createMut = useCreateSecret();
  const storageType = Form.useWatch('storageType', form) ?? 'aliyunOss';

  async function onOk() {
    const values = await form.validateFields();
    try {
      await createMut.mutateAsync(
        buildSecret({
          name: values.name.trim(),
          storageType: values.storageType,
          region: values.region.trim(),
          endpoint: values.endpoint.trim(),
          accessKey: values.accessKey.trim(),
          secretKey: values.secretKey,
        })
      );
      message.success('凭证已创建');
      handleClose();
    } catch (e) {
      message.error(fullErrorText(e));
    }
  }

  function handleClose() {
    form.resetFields();
    onClose();
  }

  return (
    <Modal
      title="新建对象存储凭证"
      open={open}
      onCancel={handleClose}
      onOk={onOk}
      okText="创建凭证"
      cancelText="取消"
      confirmLoading={createMut.isPending}
      width={520}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ storageType: 'aliyunOss' satisfies StorageTypeCase }}
      >
        <Form.Item
          label="凭证名称"
          name="name"
          rules={[{ required: true, message: '凭证名称必填' }]}
        >
          <Input placeholder="例如：生产数据存储" maxLength={64} />
        </Form.Item>

        <Form.Item
          label="存储类型"
          name="storageType"
          rules={[{ required: true, message: '存储类型必选' }]}
        >
          <Select options={STORAGE_TYPE_OPTIONS} />
        </Form.Item>

        <Form.Item
          label="区域（Region）"
          name="region"
          rules={[{ required: true, message: '区域必填' }]}
          extra="例如：cn-hangzhou、cn-beijing"
        >
          <Input placeholder="请输入 Region ID" maxLength={128} />
        </Form.Item>

        <Form.Item
          label="访问地址（Endpoint）"
          name="endpoint"
          rules={[
            { required: true, message: '访问地址必填' },
            {
              validator: (_, value: string) =>
                !value || (ENDPOINT_PATTERN.test(value.trim()) && value.trim().length <= 512)
                  ? Promise.resolve()
                  : Promise.reject(new Error('访问地址需以 http(s):// 开头，且长度不超过 512')),
            },
          ]}
          extra="请输入以 http:// 或 https:// 开头的完整 URL"
        >
          <Input placeholder={endpointPlaceholder(storageType)} maxLength={512} />
        </Form.Item>

        <Form.Item
          label="AccessKey ID"
          name="accessKey"
          rules={[{ required: true, message: 'AccessKey ID 必填' }]}
        >
          <Input placeholder="请输入 AccessKey ID" autoComplete="off" />
        </Form.Item>

        <Form.Item
          label="Secret Access Key"
          name="secretKey"
          rules={[{ required: true, message: 'Secret Access Key 必填' }]}
        >
          <Input.Password placeholder="请输入 Secret Access Key" autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
